// apps/merchant-v3/src/lib/nav/activeNav.ts
//
// Which sidebar row owns the highlight for a given path.
//
// dbg-20260911-080936 — measured across 41 merchant routes, 31 of them lit
// NOTHING in the sidebar: /products/my, /products/create, every /wallet/*,
// /billing/*, /settings/* except branding, /notifications, /artwork… The
// operator had no way to tell which page they were on.
//
// The old rule was `end` (exact match) on every row, plus one opt-in escape
// hatch (`alsoActiveOn`) that had to be declared per route. Exactly one route
// in the whole app had ever declared it. That design makes every new page a
// silent regression unless somebody remembers to come back and register it,
// and nothing enforces the reminder — so the debt piled up to 31 routes.
//
// The fix is to make "which row is active" a computed answer rather than a
// list somebody maintains: score every row against the path, most specific
// row wins.
//
//   exact         `to === pathname`             → wins over everything
//   declared      pathname under `alsoActiveOn` → beats a plain prefix
//   prefix        `pathname.startsWith(to/)`    → longest `to` wins
//
// Why not simply drop `end` and let NavLink prefix-match? Because that lights
// up a parent AND its child together (/billing and /billing/invoices), which
// is bug B10 the `end` was added to fix — and the same collision that has now
// bitten the ADMIN sidebar three separate times (dbg-20260529, dbg-20260720,
// dbg-20260730). Scoring keeps the single-active rule: there is exactly one
// winner, and it is the most specific row.
//
// `alsoActiveOn` survives because some pages belong to a row that is NOT their
// path parent, and no amount of prefix cleverness can infer that:
//   /products/create   is reached from My Products, but prefixes /products
//   /billing/*         lives in the Wallet page's tabs, sharing no prefix
//   /notifications     lives in the profile page's tabs
// Those are editorial facts about the product, so they stay declarations —
// but they are now the exception rather than the only mechanism.

/** The subset of a sidebar row this module needs. */
export interface ActiveNavCandidate {
  /** Route this row links to. */
  to: string;
  /**
   * Paths that belong to this row despite not living under {@link to}.
   *
   * Matched exactly or as a path prefix, so `/billing` covers
   * `/billing/invoices`. Use it only when the owning row cannot be derived
   * from the path — a page nested under its own row needs nothing here.
   */
  alsoActiveOn?: string[];
  /** Rows hidden by a flag or permission never take the highlight. */
  hidden?: boolean;
}

/** Scores, spaced so a stronger class always beats a weaker one's length. */
const EXACT = 1_000_000;
const DECLARED = 1_000;

/** True when `path` is `prefix` or sits underneath it. */
function underPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Score one row against a path. `0` means "not a match at all".
 *
 * Length is the tiebreaker inside a class, which is what makes `/products/my`
 * beat `/products` for `/products/my/123`.
 */
export function scoreNavCandidate(pathname: string, item: ActiveNavCandidate): number {
  if (item.hidden) return 0;

  if (item.to === pathname) return EXACT + item.to.length;

  let best = 0;
  for (const p of item.alsoActiveOn ?? []) {
    if (underPrefix(pathname, p)) best = Math.max(best, DECLARED + p.length);
  }
  if (best > 0) return best;

  // `${to}/` and not `to`: without the slash, `/products` would claim
  // `/products-archive`, a different page whose name merely starts the same.
  if (pathname.startsWith(`${item.to}/`)) return item.to.length;

  return 0;
}

/**
 * The `to` of the row that should be highlighted, or `null` when the path
 * belongs to no row at all (a standalone screen such as /onboarding).
 *
 * Ties are impossible in practice — two rows would need the same `to` — but a
 * stable answer matters more than an arbitrary one, so the first of an equal
 * pair wins rather than the last.
 */
export function resolveActiveNavTo(
  pathname: string,
  items: readonly ActiveNavCandidate[],
): string | null {
  let winner: string | null = null;
  let winnerScore = 0;
  for (const item of items) {
    const score = scoreNavCandidate(pathname, item);
    if (score > winnerScore) {
      winnerScore = score;
      winner = item.to;
    }
  }
  return winner;
}
